/**
 * HOW HIGH A THING SITS — the app's one answer to "should this have a shadow".
 *
 * Only SURFACES live here: objects that stand alone on the page. There is
 * deliberately no recipe for an input, a title, a badge, a chip or a toolbar
 * control — those are flat (the control law owns controls, and none of its
 * tones but `outline`-in-a-bar casts a shadow). If you reach for `surface` on
 * something that sits in a row of other things, it is not a surface.
 *
 * Alex, 2026-10-07, twice in one day: a surface is lifted AT REST ("showing
 * the hovering like effect with the border … before hovering"), and only
 * surfaces lift ("random things with the shadow / hovering thing like the
 * title … don't make sense"). 2026-10-20: grid tiles get "the raised meaty
 * feeling".
 *
 * - `panel`    a standalone object you look at — a dashboard block, a stat,
 *              a section holding a group. White, one hairline, the rest lift.
 * - `tile`     a standalone object you open or press — a row card, a session.
 *              A panel plus ONE hover step: the edge firms, the shadow
 *              deepens. Never movement, never an overlay's altitude.
 * - `raised`   a tile you browse in a grid. Higher at rest (contact, ambient
 *              and a low key light), a lighter hairline that never reads as a
 *              second edge (none in dark), a deeper step on hover.
 * - `selected` added to a tile that is the chosen one: the brand edge, held
 *              through hover.
 * - `overlay`  menus, popovers, ⌘K, a floating bar — over the content.
 * - `modal`    dialogs and sheets — the top of the ramp.
 *
 * Never two raised things nested: whatever sits inside a surface is flat.
 */

const PANEL = "border border-border bg-card shadow-rest";
const LIFT = "transition-[border-color,box-shadow,background-color] duration-fast ease-out";

/** The one hover step a pressable surface takes: the edge firms, the shadow
 *  deepens. Exported for the rare surface that must BE something else (a
 *  link, a button) and compose the panel itself. */
export const LIFT_STEP = `${LIFT} hover:border-edge-hover hover:shadow-raised`;

export const surface = {
  panel: PANEL,
  tile: `${PANEL} ${LIFT_STEP}`,
  raised: `border border-edge-tile bg-card shadow-tile ${LIFT} hover:border-edge-hover hover:shadow-tile-raised dark:hover:border-transparent`,
  selected: "border-primary/60 shadow-raised hover:border-primary/60",
  overlay: "border border-border bg-popover text-popover-foreground shadow-overlay",
  modal: "border border-border bg-popover text-popover-foreground shadow-modal",
} as const;

export type Surface = keyof typeof surface;
